import React, { useEffect, useState, Suspense, lazy } from 'react';
import { CircularProgress, ThemeProvider, StyledEngineProvider } from '@mui/material';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import { detect } from 'detect-browser';
import { useLocation, useNavigate, Routes, Route } from 'react-router-dom';
import NoIeSupport from './components/Helpers/Errors/Templates/NoIeSupport';
import Layout from './components/Layout/Layout';
import { setDefaultLinks } from './components/Layout/LayoutConst';
import Theme from './components/Theme/Theme';
import { getSessionStorageItem, SESSION_STORAGE_ENVIRONMENT, setSessionStorageItem } from './sessionStorage';
import { UnauthorizedPath, QuoterManagementPath } from './Routes';
import SessionTimeout from './SessionTimeout';
import AuthLandingPage from './components/Home/AuthLandingPage';
import { getAuthToken, getDevToken, redirectToLogin, validateToken } from './auth';
import ProtectedRoute from './components/Helpers/ProtectedRoute';
import { DEFAULT_ENVIRONMENT, getAvailableEnvironments } from './components/Quoter/QuoterManagement/constants';
import GlobalSnackbar from './components/Helpers/Snackbar/Snackbar';
import './Global.scss';

const browser = detect();
const isIe = browser.name === 'ie';

// Lazy-loaded components
const Welcome = lazy(() => import('./components/Home/Welcome'));
const QuoterManagement = lazy(() => import('./components/Quoter/QuoterManagement/QuoterManagement'));
const Unauthorized = lazy(() => import('./components/Common/Unauthorized'));

const PageNotFound = () => {
  return (
    <div className="container">
      <div className="jumbotron mt-4">Page not found.</div>
    </div>
  );
};


const App = () => {
  const navigate = useNavigate();
  const isAuthLanding = /^\/sso\/Authenticate(\/.*)?$/.test(window.location.pathname);

  const location = useLocation();
  const [loadingSite, setLoadingSite] = useState(true);
  const [error, setError] = useState(false);
  const [user, setUser] = useState();
  const [validAuth, setValidAuth] = useState(false);
  const [navLinks, setActiveNavLink] = useState();
  const [environment, setEnvironment] = useState(getSessionStorageItem(SESSION_STORAGE_ENVIRONMENT) || DEFAULT_ENVIRONMENT);

  const setActiveLink = (currentIndex) => {
    const navItems = navLinks;
    navItems.forEach((part, index, array) => {
      array[index].isActive = false;
    });
    navItems[currentIndex].isActive = true;

    setActiveNavLink([...navItems]);
  };

  const handleAuthentication = async () => {
    try {
      if (!getAuthToken()) {
        if (process.env.VITE_IS_DEV_ENVIRONMENT === 'true') {
          await getDevToken();
        } else {
          redirectToLogin();
          return;
        }
      }
      const isValid = await validateToken();
      setValidAuth(isValid);
      setLoadingSite(false);
    } catch (error) {
      console.error('Error during authentication:', error);
      if (process.env.VITE_IS_DEV_ENVIRONMENT !== 'true') {
        redirectToLogin();
      }
    }
  };

  useEffect(() => {
    if (isAuthLanding) {
      setLoadingSite(false);
      return;
    }
    handleAuthentication();
  }, []);

  useEffect(() => {
    if (isAuthLanding && validAuth) {
      navigate('/');
    }
  }, [validAuth]);

  useEffect(() => {
    const navItems = setDefaultLinks();
    setActiveNavLink([...navItems]);
  }, [location]);

  useEffect(() => {
    const storedEnvironment = getSessionStorageItem(SESSION_STORAGE_ENVIRONMENT);
    const availableEnvValues = getAvailableEnvironments().map(e => e.value);
    const validEnv = availableEnvValues.includes(storedEnvironment) ? storedEnvironment : DEFAULT_ENVIRONMENT;

    setSessionStorageItem(SESSION_STORAGE_ENVIRONMENT, validEnv);
    setEnvironment(validEnv);
  }, []);

  const handleEnvironmentChange = (env) => {
    setEnvironment(env);
    setSessionStorageItem(SESSION_STORAGE_ENVIRONMENT, env);
  };

  if (isIe) {
    return (
      <div>
        <NoIeSupport />
      </div>
    );
  }

  return (
    <React.Fragment>
      {(isAuthLanding) && (
        <div className="d-flex justify-content-center mt-4">
          <AuthLandingPage setValidAuth={setValidAuth} />
        </div>
      )}

      {/* Ensures there are no errors, the site is not loading, and the user is not on the auth landing page,
          Checks that the user is available and either has valid authentication or authentication bypass enabled,
          Ensures sign-on is not invalid, no role-based errors exist, and the user is not logged out.
          Also allows rendering if "sign-in without authentication" is enabled and an account exists. */}

      {(!error && !loadingSite && !isAuthLanding && validAuth) && (
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={Theme.get()}>
            <StylesThemeProvider theme={Theme.get()}>
              <Layout
                navLinks={navLinks}
                iscName={user && user.iscName}
                setActiveNavLink={setActiveLink}
                environment={environment}
                onEnvironmentChange={handleEnvironmentChange}
              >
                <Suspense fallback={<div className="d-flex justify-content-center p-5">
                  <CircularProgress /></div>}>
                  <Routes>
                    <Route
                      path="/"
                      element={<ProtectedRoute setActiveLink={setActiveLink} navLinks={navLinks}><Welcome /></ProtectedRoute>}
                    />
                    <Route
                      path={QuoterManagementPath}
                      element={
                        <ProtectedRoute setActiveLink={setActiveLink} navLinks={navLinks}>
                          <QuoterManagement key={environment} environment={environment} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={UnauthorizedPath}
                      element={<ProtectedRoute setActiveLink={setActiveLink} navLinks={navLinks}><Unauthorized /></ProtectedRoute>}
                    />
                    <Route path="*" element={<PageNotFound />} />
                  </Routes>
                </Suspense>
              </Layout>
            </StylesThemeProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      )}
      {loadingSite && (
        <div className="d-flex justify-content-center mt-4">
          <CircularProgress />
        </div>
      )}
      {!loadingSite && validAuth && !isAuthLanding && (
        <SessionTimeout />
      )}
      <GlobalSnackbar />
    </React.Fragment>
  );
};

export default App;
